import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { notifications } from '@/utils/notifications';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  uid: string;
  userName: string;
  onSuccess: (uid: string) => void;
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  onClose,
  uid,
  userName,
  onSuccess,
}) => {
  const { t } = useTranslation('common');
  const { t: tToast } = useTranslation('notifications');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      const response = await api.delete('/users', { data: { uid: uid } });
      if (response.data.status === 'success') {
        notifications.success(
          response.data.message || tToast('deleteUser.removedFallback', { name: userName })
        );
        onSuccess(uid);
        onClose();
      } else {
        throw new Error(response.data.message || t('shared.modals.deleteUser.removeFailed'));
      }
    } catch (err: unknown) {
      console.error('Failed to remove user:', err);
      let message = t('shared.modals.deleteUser.removeFailedRetry');
      if (err instanceof Error) message = err.message;
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('shared.modals.deleteUser.title')}
      confirmLabel={t('shared.modals.deleteUser.confirmLabel')}
      confirmingLabel={t('shared.modals.deleteUser.confirming')}
      isConfirming={isDeleting}
      onConfirm={handleDelete}
      error={error}
      size="lg"
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('shared.modals.deleteUser.confirmText')}{' '}
          <span className="text-foreground font-bold italic">"{userName}"</span> from this account?
        </p>
        <p className="text-muted-foreground/60 text-xs font-medium italic">
          {t('shared.modals.deleteUser.warning')}
        </p>
      </div>
    </ConfirmModal>
  );
};
